import type { Maybe } from '@adornis/base/utilTypes.js';
import { toCalendarDay } from '@adornis/datetime/calendarDay.js';

/**
 * Booking runtimes are inclusive canonical calendar days — 00:00:00.000 UTC of the first and last
 * included day (see `@adornis/datetime/calendarDay.js`). The reference instant is normalized to its
 * German calendar day, then everything compares as plain instants.
 */
export function bookingActive(booking: { startDate: Maybe<Date>; endDate: Maybe<Date> }, date: Date = new Date()) {
  const day = toCalendarDay(date);

  // if nothing is set as the start the booking should be active at all times in the past
  const start = booking.startDate ?? new Date(0);

  return start <= day && (!booking.endDate || booking.endDate >= day);
}

/** True if the booking starts after the given day — a booking starting that very day is active, not future. */
export function bookingFuture(booking: { startDate: Maybe<Date> }, date: Date = new Date()) {
  const day = toCalendarDay(date);

  const start = booking.startDate ?? new Date(0);

  return start > day;
}

/**
 * Returns an array of MongoDB selector parts that can be combined with $and to filter bookings that are active on a given date.
 * @param date The date to check against (defaults to today)
 * @returns An array of selector objects
 */
export function getActiveBookingMongoSelector(date: Date = new Date()): Record<string, unknown>[] {
  const day = toCalendarDay(date);

  return [
    { $or: [{ startDate: null }, { startDate: { $lte: day } }, { startDate: { $exists: false } }] },
    { $or: [{ endDate: null }, { endDate: { $gte: day } }, { endDate: { $exists: false } }] },
  ];
}

/**
 * Bookings of the same person on every group of the given booking whose runtime touches its runtime -
 * the ones that already make them a member of those groups for some of the same days. The range
 * counterpart of {@link getActiveBookingMongoSelector}; an open side reaches everything beyond it.
 */
export function getOverlappingBookingSelector(booking: {
  userID: Maybe<string>;
  groupIDs: Maybe<string[]>;
  startDate: Maybe<Date>;
  endDate: Maybe<Date>;
}): Record<string, unknown> {
  const runtime: Record<string, unknown>[] = [];
  if (booking.endDate)
    runtime.push({
      $or: [
        { startDate: null },
        { startDate: { $lte: toCalendarDay(booking.endDate) } },
        { startDate: { $exists: false } },
      ],
    });
  if (booking.startDate)
    runtime.push({
      $or: [
        { endDate: null },
        { endDate: { $gte: toCalendarDay(booking.startDate) } },
        { endDate: { $exists: false } },
      ],
    });

  return {
    userID: booking.userID,
    groupIDs: { $all: booking.groupIDs ?? [] },
    ...(runtime.length ? { $and: runtime } : {}),
  };
}
